import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const BrandingTab = ({ practitionerData, onSave }) => {
  const [formData, setFormData] = useState({
    practiceName: '',
    practitionerTitle: '',
    practitionerName: '',
    logoUrl: '',
    logoInitials: '',
    logoPosition: 'left',
    logoSize: 'medium',
    signatureUrl: ''
  });
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);

  useEffect(() => {
    if (practitionerData) {
      setFormData({
        practiceName: practitionerData?.practiceName || '',
        practitionerTitle: practitionerData?.practitionerTitle || '',
        practitionerName: practitionerData?.practitionerName || '',
        logoUrl: practitionerData?.logoUrl || '',
        logoInitials: practitionerData?.logoInitials || '',
        logoPosition: practitionerData?.logoPosition || 'left',
        logoSize: practitionerData?.logoSize || 'medium',
        signatureUrl: practitionerData?.signatureUrl || ''
      });
    }
  }, [practitionerData]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setHasChanges(true);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
      setHasChanges(false);
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewPDF = async () => {
    try {
      setShowPreview(true);
      // Dynamically import pdfGenerator to avoid loading it on initial render
      const { generateReferralPDFBlob } = await import('../../../utils/pdfGenerator');
      
      // Create sample referral data for preview with correct structure
      const sampleReferralData = {
        patientInfo: {
          firstName: 'Jean',
          lastName: 'Dupont',
          yearOfBirth: 1980,
          sex: 'male',
          phone: '06 12 34 56 78',
          email: 'jean.dupont@example.com'
        },
        referralReasons: ['pulpite_irreversible'],
        toothNumbers: ['16'],
        clinicalNotes: 'Exemple de notes cliniques pour la prévisualisation du PDF. Le patient présente une douleur persistante sur la dent 16.',
        referringDentist: {
          fullName: 'Dr. Martin Dubois',
          practiceName: 'Cabinet Dentaire Centre-Ville',
          phone: '01 23 45 67 89',
          email: 'dr.martin@example.com'
        },
        imaging: {
          downloadLink: 'https://example.com/imaging',
          imagingCenter: 'Centre d\'Imagerie Médicale'
        }
      };

      // Generate PDF with current form data
      const pdfBlob = await generateReferralPDFBlob(sampleReferralData, formData);
      
      if (!pdfBlob) {
        throw new Error('La génération du PDF a échoué');
      }
      
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPreviewPdfUrl(pdfUrl);
    } catch (error) {
      console.error('Error generating PDF preview:', error);
      alert('Erreur lors de la génération de l\'aperçu PDF: ' + (error?.message || 'Erreur inconnue'));
      setShowPreview(false);
    }
  };

  const closePreview = () => {
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl(null);
    }
    setShowPreview(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Practice Information */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="Building" size={20} className="text-accent" />
          Informations du cabinet
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Nom du cabinet"
            name="practiceName"
            value={formData?.practiceName}
            onChange={handleInputChange}
            placeholder="Cabinet d'Endodontie"
            required
          />
          <Input
            label="Titre du praticien"
            name="practitionerTitle"
            value={formData?.practitionerTitle}
            onChange={handleInputChange}
            placeholder="Dr."
          />
          <div className="md:col-span-2">
            <Input
              label="Nom du praticien"
              name="practitionerName"
              value={formData?.practitionerName}
              onChange={handleInputChange}
              placeholder="Jean Dupont"
              required
            />
          </div>
        </div>
      </div>
      {/* Logo Configuration */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="Image" size={20} className="text-accent" />
          Logo du cabinet
        </h3>
        <div className="space-y-6">
          {/* Logo URL */}
          <div>
            <Input
              label="URL du logo"
              name="logoUrl"
              type="url"
              value={formData?.logoUrl}
              onChange={handleInputChange}
              placeholder="https://exemple.com/logo.png"
            />
            <p className="text-xs text-muted mt-2">
              Le logo apparaîtra sur vos PDF (aperçu et document final). Si aucun logo n'est fourni, vos initiales seront utilisées.
            </p>
            {formData?.logoUrl && (
              <div className="flex items-center gap-4 p-4 bg-card-elevated rounded-xl border border-white/8 mt-4">
                <img 
                  src={formData?.logoUrl} 
                  alt="Logo preview" 
                  className="w-20 h-20 object-contain rounded-lg bg-white border border-gray-200"
                  onError={(e) => {
                    e.target.src = '/assets/images/no_image.png';
                  }}
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-primary">Aperçu du logo</p>
                  <p className="text-xs text-muted">Ce logo apparaîtra dans vos documents PDF</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Logo Initials (Fallback) */}
          <div className="border-t border-white/8 pt-6">
            <h4 className="text-sm font-semibold text-primary mb-3">Initiales (affichées si aucun logo)</h4>
            <p className="text-xs text-muted mb-4">
              Ces initiales seront utilisées comme logo de secours dans vos PDF si aucune URL de logo n'est fournie.
            </p>
            <Input
              label="Initiales (1-3 caractères)"
              name="logoInitials"
              type="text"
              maxLength={3}
              value={formData?.logoInitials}
              onChange={handleInputChange}
              placeholder="AT"
            />
            {formData?.logoInitials && (
              <div className="mt-4 flex items-center gap-4 p-4 bg-card-elevated rounded-lg border border-white/8">
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center shadow-lg bg-accent"
                >
                  <span className="text-2xl font-bold text-white">
                    {formData?.logoInitials?.toUpperCase()}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-primary">Aperçu des initiales</p>
                  <p className="text-xs text-muted">Utilisées comme logo de secours dans vos PDF</p>
                </div>
              </div>
            )}
          </div>

          {/* Logo Customization Options */}
          <div className="border-t border-white/8 pt-6">
            <h4 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
              <Icon name="Settings" size={16} className="text-accent" />
              Personnalisation du logo sur le PDF
            </h4>
            <p className="text-xs text-muted mb-4">
              Choisissez la position et la taille du logo sur vos documents PDF.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Logo Position */}
              <div>
                <label className="block text-sm font-medium text-primary mb-2">
                  Position du logo
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'left', label: 'Gauche', icon: 'AlignLeft' },
                    { value: 'center', label: 'Centre', icon: 'AlignCenter' },
                    { value: 'right', label: 'Droite', icon: 'AlignRight' }
                  ]?.map((option) => (
                    <button
                      key={option?.value}
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, logoPosition: option?.value }));
                        setHasChanges(true);
                      }}
                      className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                        formData?.logoPosition === option?.value
                          ? 'border-accent bg-accent/10 text-accent' :'border-white/8 bg-card-elevated text-muted hover:border-accent/50'
                      }`}
                    >
                      <Icon name={option?.icon} size={20} />
                      <span className="text-xs font-medium">{option?.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Logo Size */}
              <div>
                <label className="block text-sm font-medium text-primary mb-2">
                  Taille du logo
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { value: 'small', label: 'Petit', size: 16 },
                    { value: 'medium', label: 'Moyen', size: 20 },
                    { value: 'large', label: 'Grand', size: 24 },
                    { value: 'xlarge', label: 'Très Grand', size: 28 }
                  ]?.map((option) => (
                    <button
                      key={option?.value}
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, logoSize: option?.value }));
                        setHasChanges(true);
                      }}
                      className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                        formData?.logoSize === option?.value
                          ? 'border-accent bg-accent/10 text-accent' :'border-white/8 bg-card-elevated text-muted hover:border-accent/50'
                      }`}
                    >
                      <Icon name="Image" size={option?.size} />
                      <span className="text-xs font-medium">{option?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Preview Button */}
            <div className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={handlePreviewPDF}
                iconName="Eye"
                className="w-full md:w-auto"
              >
                Prévisualiser le PDF
              </Button>
              <p className="text-xs text-muted mt-2">
                Visualisez l'apparence de votre logo sur un document PDF avant d'enregistrer.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Signature Configuration */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="PenTool" size={20} className="text-accent" />
          Signature personnalisée
        </h3>
        <div className="space-y-4">
          <Input
            label="URL de la signature"
            name="signatureUrl"
            type="url"
            value={formData?.signatureUrl}
            onChange={handleInputChange}
            placeholder="https://exemple.com/signature.png"
          />
          <p className="text-xs text-muted">
            Ajoutez votre signature personnalisée qui apparaîtra au bas de vos documents PDF pour un rendu professionnel.
          </p>
          {formData?.signatureUrl && (
            <div className="flex items-center gap-4 p-4 bg-card-elevated rounded-xl border border-white/8">
              <img 
                src={formData?.signatureUrl} 
                alt="Signature preview" 
                className="h-16 object-contain rounded-lg bg-white border border-gray-200 px-4"
                onError={(e) => {
                  e.target.src = '/assets/images/no_image.png';
                }}
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-primary">Aperçu de la signature</p>
                <p className="text-xs text-muted">Cette signature apparaîtra dans vos documents PDF</p>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Actions */}
      <div className="flex items-center justify-end gap-4 pt-6 border-t border-white/8">
        <Button
          type="button"
          variant="outline"
          onClick={() => window.location?.reload()}
          disabled={!hasChanges || saving}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          loading={saving}
          disabled={!hasChanges || saving}
          iconName="Save"
        >
          {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </Button>
      </div>
      {/* PDF Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-card-elevated rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/8">
              <div className="flex items-center gap-3">
                <Icon name="FileText" size={24} className="text-accent" />
                <div>
                  <h3 className="text-lg font-semibold text-primary">Aperçu du PDF</h3>
                  <p className="text-sm text-muted">Prévisualisation de votre logo sur le document</p>
                </div>
              </div>
              <button
                onClick={closePreview}
                className="p-2 rounded-lg hover:bg-white/5 transition-colors"
              >
                <Icon name="X" size={24} className="text-muted" />
              </button>
            </div>

            {/* PDF Viewer */}
            <div className="flex-1 overflow-auto p-6">
              {previewPdfUrl ? (
                <iframe
                  src={previewPdfUrl}
                  className="w-full h-full min-h-[600px] rounded-lg border border-white/8"
                  title="PDF Preview"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Icon name="Loader" size={48} className="text-accent animate-spin mx-auto mb-4" />
                    <p className="text-muted">Génération de l'aperçu...</p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-white/8 flex justify-end">
              <Button
                type="button"
                onClick={closePreview}
                iconName="X"
              >
                Fermer l'aperçu
              </Button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default BrandingTab;